import type { ModuleAPI } from "./dotnet";
import { onMounted } from "vue";
import { asError } from "@/common/lib";
import { onDocumentFinishedLoading, onDocumentStartedLoading } from "./bridge/document";
import { onBridgeReady } from "./bridge/editor";
import { useDotnetLoadingProgress } from "./composables/useDotnetLoadingProgress";
import { setupImports } from "./interop";
import { useDocumentStore } from "./store/document";

const { setProgress } = useDotnetLoadingProgress();
function onDownloadResourceProgress(loaded: number, total: number) {
  setProgress({
    loaded,
    total,
  });
}

async function start() {
  // dotnet is compiled separately and is currently bundled next to JS so it's imported using native import()
  // 'dotnetJSPath' variable is required to prevent vite from analyzing this import and to bundle it 'as is' during build step
  // use git hash for cache busting in production
  const dotnetJSPath = import.meta.env.PROD ? `/compiled-wasm/_framework/dotnet.${import.meta.env.VITE_GIT_HASH}.js` : "./wasm/dotnet.js";
  const moduleApi = await import(/* @vite-ignore */ dotnetJSPath) as ModuleAPI;

  const { dotnet } = moduleApi;
  const builder = dotnet
    .withDiagnosticTracing(false);

  // @ts-expect-error `withModuleConfig` is internal
  builder.withModuleConfig({
    onDownloadResourceProgress,
  });

  const runtime = await builder.create();
  globalThis.Module = runtime.Module;

  setupImports(runtime);

  await dotnet.withDiagnosticTracing(true).withDebugging(1).run();
}

export function useDiskoDotnetRuntimeGuard() {
  // Watches lifecycle events from the dotnet runtime and updates the document store's runtime state accordingly.
  const store = useDocumentStore();

  onBridgeReady(() => {
    store.runtimeState = "ready";
  });

  onDocumentStartedLoading(() => {
    store.state = "loading";
  });

  onDocumentFinishedLoading(() => {
    store.state = "ready";
  });
}

export function useDiskoDotnetRuntime() {
  const store = useDocumentStore();

  onMounted(async () => {
    if (store.runtimeState === "idle") {
      try {
        store.runtimeState = "starting";
        await start();
      } catch (error) {
        console.error("Error starting the dotnet runtime:", error);
        store.runtimeState = "error";
        store.runtimeLoadingError = asError(error);
      }
    }
  });
}
